'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useWorkspaceActions } from './store';
import { useAuth } from '@/components/auth-provider';
import type { WorkspaceItemDTO, WorkspaceItemType } from '@/lib/workspaces/types';
import { isShellItemType } from '@/lib/workspaces/types';
import { resolveWidget } from '@/lib/workspaces/widget-registry';
import { widgetIconString } from '@/lib/content/app-identity';
import { markFirstRunDone } from '@/lib/workspaces/first-run';
import { recordContentOpen } from '@/lib/workspaces/recent-opens';

/**
 * Card-action hand-off between feed surfaces (PostCard,
 * AgentDiscoverCard, PostDiscoverCard, etc.) and the workspace they're
 * embedded in.
 *
 * The cards themselves are shared with /discover, /marketplace, and a
 * handful of profile pages where the "chat" affordance is what the
 * user actually wants (jump into a draft chat with the agent). Inside
 * the workspace canvas the same affordance reads differently: the
 * user has a spatial board, so the natural action is to drop the
 * agent (or widget) onto it rather than spin up an unrelated chat
 * thread.
 *
 * This context is mounted only by `WorkspaceShell`. When present, the
 * cards swap their chat icon for an "Add to workspace" verb and call
 * back here; when absent (every non-workspace surface) the cards keep
 * their original chat-sidebar behaviour. Keeping the cards
 * context-aware avoids a parallel `WorkspacePostCard` copy that would
 * drift over time.
 */
export type WorkspaceCardActions = {
  /**
   * Add an agent widget to the workspace (or focus it if already
   * present).
   *
   * When `chatId` is passed, the spawned widget is bound to that
   * chat from the first paint — `state.chatId` lands in the
   * persisted row and the embedded `ChatView` renders the live
   * conversation instead of the agent's draft welcome. Used by
   * the `/chats/<id>` legacy redirect and the smoke test
   * (which creates a chat via API, then deep-links into the
   * workspace with the id pre-bound so the journey can target
   * `[data-chat-id="…"]` deterministically).
   */
  addAgent: (info: {
    agentId: string;
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
    /**
     * Participant kind, cached on the item so `AgentNode` can render a
     * person as a person (no Settings tab, human avatar) instead of
     * falling back to agent chrome. Values are the `users.account_type`
     * enum, NOT a widget-local vocabulary — a mismatch here is what kept
     * the human branch dead the first time round. Omitted means "agent",
     * matching every pre-existing item.
     */
    accountType?: 'human' | 'agent';
    chatId?: string | null;
    /** See `store.addItem`'s `screen`. Callers that tile the result pass 'current'. */
    screen?: 'new' | 'current';
  }) => Promise<WorkspaceItemDTO | null>;
  /**
   * Add a widget content item to the workspace (community widget
   * published as a post). The caller passes the underlying contentId
   * that backs the widget — built-ins, system-seeded starters, and
   * community submissions all flow through the same contentId path
   * used by the Zap → Discover (`contentType=widget`) feed.
   *
   * `metadata` is the widget content row's `metadata` blob (as returned
   * by /api/posts / /api/discovery/posts via the widget LEFT JOIN).
   * When `metadata.kind === 'native'` and `metadata.itemType` resolves
   * to one of the native workspace types (python / ssh / daemon), we
   * dispatch as that item type instead of the generic `content` path
   * so the canvas mounts the proper React component. iframe and
   * projection-only widgets fall through to the existing content path.
   *
   * `sourceWidgetPostId` (when provided) is stashed in
   * `state.cache.sourceWidgetPostId` so the workspace items POST route
   * can attribute the install back to the marketplace post and bump
   * `posts.install_count`. Agent-driven spawns deliberately leave this
   * unset so they don't inflate the popularity counter.
   */
  addWidget: (info: {
    contentId: string;
    title?: string | null;
    thumbnailUrl?: string | null;
    metadata?: Record<string, unknown> | null;
    sourceWidgetPostId?: string | null;
    /** See `store.addItem`'s `screen`. Callers that tile the result pass 'current'. */
    screen?: 'new' | 'current';
  }) => Promise<WorkspaceItemDTO | null>;
};

const NATIVE_WIDGET_TYPES: ReadonlySet<string> = new Set(['python', 'ssh', 'daemon', 'files']);

const Ctx = createContext<WorkspaceCardActions | null>(null);

/**
 * Wire WorkspaceCardActions to the workspace store. Renders nothing —
 * just installs the context value so descendant cards can opt into
 * workspace-aware behaviour. Mount once near the top of
 * `WorkspaceShell`, before any feed / search / panel children.
 */
export function WorkspaceCardActionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { addItem } = useWorkspaceActions();
  // First-run latch: any spawn through the card actions is a self-initiated
  // add (a click on a card, a starter, a search result), which is the signal
  // that retires the Discover starter band for this account. An app the
  // FUNNEL placed never comes through here, so funnel arrivals still meet
  // the starters on their first empty screen.
  const { user } = useAuth();
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = user?.id ?? null;

  // Land the camera on the newly-spawned widget via the ws:focus-item
  // chokepoint — which frames the widget's SCREEN and does nothing
  // when the camera is already parked there (Part X: a spawn onto the
  // current screen must not so much as twitch the view). The old
  // direct `flow.setCenter` on the widget's centroid bypassed the
  // screens model and nudged the camera on every catalog open.
  const focusOnSpawn = useCallback(
    (
      item:
        | {
            id?: string;
            state: { x: number; y: number; width: number; height: number };
          }
        | null
        | undefined,
    ) => {
      if (!item?.id) return;
      window.dispatchEvent(
        new CustomEvent('ws:focus-item', { detail: { itemId: item.id } }),
      );
    },
    [],
  );

  const addAgent = useCallback<WorkspaceCardActions['addAgent']>(
    (info) => {
      return addItem({
        type: 'agent',
        ...(info.screen ? { screen: info.screen } : {}),
        refId: info.agentId,
        state: {
          // Bind the widget to the existing chat at spawn time so
          // `ChatView` opens directly into the live conversation
          // (skipping the agent's draft welcome). Skipped when the
          // caller didn't supply a chat id — the welcome flow is
          // the right default for fresh agent adds.
          ...(info.chatId ? { chatId: info.chatId } : {}),
          cache: {
            displayName: info.displayName ?? info.username,
            username: info.username,
            avatarUrl: info.avatarUrl ?? null,
            ...(info.accountType ? { accountType: info.accountType } : {}),
          },
        },
      }).then((item) => {
        focusOnSpawn(item);
        markFirstRunDone(userIdRef.current);
        return item;
      }).catch((err) => {
        // Spawn is best-effort (addItem already rolled back the optimistic
        // item), but a silent drop made one-shot callers (deep links)
        // undiagnosable — keep the failure visible in the console.
        console.warn(`[workspace] agent spawn failed: ${(err as Error)?.message ?? err}`);
        return null;
      });
    },
    [addItem, focusOnSpawn],
  );

  const addWidget = useCallback<WorkspaceCardActions['addWidget']>(
    (info) => {
      const meta = info.metadata ?? undefined;
      const kind = typeof meta?.kind === 'string' ? meta.kind : undefined;
      // Native widgets (python / ssh / daemon) use a dedicated
      // workspace item type so the canvas mounts the right React
      // component — not the generic ContentNode that an iframe widget
      // gets. We resolve `itemType` from the widget metadata (set by
      // seed-system-widgets.ts) and only honour it when it names one
      // of the known native types, so a malformed manifest can't
      // smuggle a non-widget item type.
      const itemTypeRaw = typeof meta?.itemType === 'string' ? meta.itemType : undefined;
      if (kind === 'native' && itemTypeRaw && NATIVE_WIDGET_TYPES.has(itemTypeRaw)) {
        const itemType = itemTypeRaw as WorkspaceItemType;
        // Native widgets spawn as their own workspace item type (no
        // backing content row). Shells mount in ShellFrame; `files`
        // mounts in NodeFrame and seeds an empty `state.files` slice so
        // it opens at the workspace root.
        if (isShellItemType(itemType) || itemType === 'files') {
          return addItem({
            type: itemType,
            ...(info.screen ? { screen: info.screen } : {}),
            state: {
              ...(itemType === 'files' ? { files: {} } : {}),
              cache: {
                displayName: info.title ?? itemTypeRaw,
                ...(info.sourceWidgetPostId
                  ? { sourceWidgetPostId: info.sourceWidgetPostId }
                  : {}),
              },
            },
          }).then((item) => {
            focusOnSpawn(item);
            markFirstRunDone(userIdRef.current);
            return item;
          }).catch(() => null);
        }
      }
      // Resolve the widget's declared canvas size from its metadata
      // so iframe / projection-only widgets land at their registry
      // dimensions (snake 360×420, calculator 320×420, weather
      // 320×200, …) instead of the generic content default (360×360).
      // Without this every iframe widget gets the same shape on
      // instantiation and the user has to manually resize it before
      // the widget body even fits.
      const resolvedSpec = resolveWidget(meta ?? null);
      const spawnSize = resolvedSpec
        ? {
            size: resolvedSpec.size,
            minSize: resolvedSpec.minSize ?? null,
            maxSize: resolvedSpec.maxSize ?? null,
          }
        : undefined;
      return addItem({
        type: 'content',
        ...(info.screen ? { screen: info.screen } : {}),
        // A HINT, not a pin: pinning it here counted as a caller opinion and
        // skipped the expand pass, so apps always opened at exactly their
        // registry size no matter how much screen there was.
        ...(spawnSize ? { sizeHint: spawnSize } : {}),
        refId: info.contentId,
        state: {
          cache: {
            displayName: info.title ?? 'Widget',
            contentType: 'widget',
            previewUrl: info.thumbnailUrl ?? null,
            // The app's declared mark + stable gradient seed, so the taskbar
            // puck renders identity immediately (no wait for resolve-ref).
            ...(widgetIconString(meta ?? null)
              ? { appIcon: widgetIconString(meta ?? null) }
              : {}),
            ...(meta && typeof (meta as Record<string, unknown>).widgetType === 'string'
              ? { widgetType: (meta as Record<string, unknown>).widgetType }
              : {}),
            ...(info.sourceWidgetPostId
              ? { sourceWidgetPostId: info.sourceWidgetPostId }
              : {}),
          },
        },
      }).then((item) => {
        focusOnSpawn(item);
        markFirstRunDone(userIdRef.current);
        // The honest Recent signal: the user OPENED this content.
        recordContentOpen(info.contentId);
        return item;
      }).catch(() => null);
    },
    [addItem, focusOnSpawn],
  );

  const value = useMemo<WorkspaceCardActions>(
    () => ({ addAgent, addWidget }),
    [addAgent, addWidget],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Read the workspace card-actions, if available. Returns `null` on
 * every non-workspace surface — callers must fall back to their
 * default behaviour (typically opening the chat sidebar) when this is
 * the case.
 */
export function useWorkspaceCardActions(): WorkspaceCardActions | null {
  return useContext(Ctx);
}
